import axios from 'axios';
import config from '../config';

// Create axios instance
const axiosInstance = axios.create({
    baseURL: config.apiUrl,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Request interceptor to add JWT token
axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Queue of requests waiting for token refresh
let isRefreshing = false;
let refreshQueue = [];

function processQueue(error, token = null) {
    refreshQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error);
        } else {
            resolve(token);
        }
    });
    refreshQueue = [];
}

function redirectToLogin() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    window.location.href = '/login';
}

// Response interceptor to handle token refresh
axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status !== 401 || originalRequest._retry) {
            return Promise.reject(error);
        }

        const errorCode = error.response?.data?.error_code;

        // Token blacklisted or permanently invalid — no point refreshing
        if (errorCode === 'TOKEN_BLACKLISTED' || errorCode === 'TOKEN_INVALID') {
            redirectToLogin();
            return Promise.reject(error);
        }

        if (isRefreshing) {
            // Another refresh is in progress — queue this request
            return new Promise((resolve, reject) => {
                refreshQueue.push({ resolve, reject });
            }).then((newToken) => {
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return axiosInstance(originalRequest);
            }).catch((err) => {
                return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const token = localStorage.getItem('token');
        if (!token) {
            isRefreshing = false;
            redirectToLogin();
            return Promise.reject(error);
        }

        try {
            const response = await axios.post(
                `${config.apiUrl}/api/v1/refresh`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success && response.data.data.token) {
                const newToken = response.data.data.token;
                localStorage.setItem('token', newToken);
                if (response.data.user) {
                    const permissions = response.data.permissions || [];
                    localStorage.setItem('user', JSON.stringify({ ...response.data.user, permissions }));
                }
                axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                originalRequest.headers.Authorization = `Bearer ${newToken}`;

                processQueue(null, newToken);
                return axiosInstance(originalRequest);
            }

            processQueue(new Error('Refresh failed'), null);
            redirectToLogin();
            return Promise.reject(error);
        } catch (refreshError) {
            processQueue(refreshError, null);
            redirectToLogin();
            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }
);

export default axiosInstance;
